import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type * as PopperJS from '@popperjs/core';
import { usePopper } from 'react-popper';
import { useThemeContext } from '../../../utils/providers/theme-context';

type TooltipProps = {
  label: React.ReactElement | string;
  placement?: PopperJS.Placement;
  enterDelay?: number;
  leaveDelay?: number;
} & React.HTMLAttributes<HTMLDivElement>;

export const Tooltip: React.FC<TooltipProps> = props => {
  const {
    children,
    label,
    enterDelay = 250,
    leaveDelay = 200,
    placement = 'bottom'
  } = props;

  const { getColour } = useThemeContext();
  const [isOpen, setIsOpen] = useState(false);
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [{ name: 'offset', options: { offset: [0, 4.5] } }]
  });

  const enterTimeout = useRef<NodeJS.Timeout>();
  const leaveTimeout = useRef<NodeJS.Timeout>();
  const handleMouseEnter = useCallback(() => {
    leaveTimeout.current && clearTimeout(leaveTimeout.current);
    enterTimeout.current = setTimeout(() => setIsOpen(true), enterDelay);
  }, [enterDelay]);
  const handleMouseLeave = useCallback(() => {
    enterTimeout.current && clearTimeout(enterTimeout.current);
    leaveTimeout.current = setTimeout(() => setIsOpen(false), leaveDelay);
  }, [leaveDelay]);

  return (
    <>
      <div
        ref={setReferenceElement}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="flex"
      >
        {children}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={setPopperElement}
            style={{
              ...styles.popper,
              backgroundColor: getColour('secondLayer'),
              color: getColour('normalText'),
              border: `1px solid ${getColour('hairline')}`
            }}
            {...attributes.popper}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="transition-opacity flex justify-center items-center px-3 py-2 absolute z-[9999] text-xs rounded-lg shadow-pop opacity-100"
          >
            <span>{label}</span>
          </div>,
          document.body
        )}
    </>
  );
};
